import { Filter } from '@empathy/search-types';
import { Facets, SearchState } from '../../../models';
import { Dictionary, isArrayEmptyOrNull } from '../../../utils';
import { GetFacetConfigByName } from '../../config/getters/facet.getter';
import { GETTERS_KEYS } from '../../store.const';
import { RootStoreState } from '../../store.interfaces';
import { SEARCH_GETTERS_KEYS } from '../search.const';
import { SearchGetter } from '../search.interfaces';

export class SelectedFiltersGetter implements SearchGetter {

  getters = {
    [SEARCH_GETTERS_KEYS.selectedFiltersByFacet]: (searchState: SearchState, getters: any, rootState: RootStoreState,
      rootGetters: any): Dictionary<Filter[]> => {
      const getFacetConfig: GetFacetConfigByName = rootGetters[GETTERS_KEYS.config.facet];
      return Object.entries(searchState.facets).reduce((selectedFiltersByFacet, [facetName, facet]) => {
        const selectedFacetFilters = this.getSelectedFilters(facet.filters);
        if (!isArrayEmptyOrNull(selectedFacetFilters) || isArrayEmptyOrNull(selectedFiltersByFacet[facetName])) {
          selectedFiltersByFacet[facetName] = selectedFacetFilters;
        }
        return selectedFiltersByFacet;
      }, this.getCompulsoryFilters(searchState, getFacetConfig));
    },
    [SEARCH_GETTERS_KEYS.selectedRootFiltersByFacet]: (searchState: SearchState, getters: any): Dictionary<Filter[]> => {
      const filtersByFacet: Dictionary<Filter[]> = getters[SEARCH_GETTERS_KEYS.selectedFiltersByFacet];
      return Object.entries(filtersByFacet).reduce<Dictionary<Filter[]>>((rootFilters, [facetName, filters]) => {
        rootFilters[facetName] = filters.map(filter => {
          let rootFilter = filter;
          while (rootFilter.parent) {
            rootFilter = rootFilter.parent;
          }
          return rootFilter;
        });
        return rootFilters;
      }, {});
    },
    [SEARCH_GETTERS_KEYS.selectedFilters]: (searchState: SearchState, getters: any): Filter[] => {
      const selectedFiltersByFacet: Dictionary<Filter[]> = getters[SEARCH_GETTERS_KEYS.selectedFiltersByFacet];
      return Object.values(selectedFiltersByFacet)
        .reduce((selectedFilters, filtersPerFacet) => selectedFilters.concat(filtersPerFacet), []);
    },
    [SEARCH_GETTERS_KEYS.selectedFiltersByGroup]: (searchState: SearchState, getters: any): Dictionary<Filter[]> => {
      const selectedFiltersByFacet: Dictionary<Filter[]> = getters[SEARCH_GETTERS_KEYS.selectedFiltersByFacet];
      const facetsGroups = searchState.facetsGroups;
      return Object.entries(facetsGroups)
        .reduce<Dictionary<Filter[]>>((selectedFiltersByGroup, [groupName, facetGroup]) => {
          selectedFiltersByGroup[groupName] = Object.keys(facetGroup.facets).reduce((groupSelectedFilters, facetName) => {
            return groupSelectedFilters.concat(selectedFiltersByFacet[facetName] ?? []);
          }, []);
          return selectedFiltersByGroup;
        }, {});
    },
    [SEARCH_GETTERS_KEYS.selectedFiltersCount]: (searchState: SearchState, getters: any): Dictionary<number> => {
      const selectedFiltersByFacet: Dictionary<Filter[]> = getters[SEARCH_GETTERS_KEYS.selectedFiltersByFacet];
      return Object.entries(selectedFiltersByFacet).reduce(this.countSelectedFilters, {});
    },
    [SEARCH_GETTERS_KEYS.compulsoryFacets]:
      (searchState: SearchState, getters: any, rootState: RootStoreState, rootGetters: any): Facets => {
        const getFacetConfig: GetFacetConfigByName = rootGetters[GETTERS_KEYS.config.facet];
        return Object.entries(searchState.facets).reduce<Facets>((compulsoryFacets, [facetName, facet]) => {
          if (getFacetConfig(facetName).compulsory) {
            compulsoryFacets[facetName] = facet;
          }
          return compulsoryFacets;
        }, {});
      }
  };

  private getSelectedFilters(filters: Filter[]): Filter[] {
    return filters.filter((filter: Filter) => filter.selected)
      .reduce((selectedFilters: Filter[], filter: Filter) => {
        if (filter.children) {
          return selectedFilters.concat(this.getLeafSelectedFilters(filter));
        } else {
          selectedFilters.push(filter);
          return selectedFilters;
        }
      }, []);
  }

  private getLeafSelectedFilters(filter: Filter): Filter[] {
    const selectedChildrenFilters = this.getSelectedFilters(filter.children);
    if (selectedChildrenFilters.length > 0) {
      return selectedChildrenFilters;
    } else {
      return [filter];
    }
  }

  private countSelectedFilters(selectedFiltersCount: Dictionary<number>, [facetName, selectedFilters]: [string, Filter[]]) {
    selectedFiltersCount[facetName] = selectedFilters.length;
    return selectedFiltersCount;
  }

  private getCompulsoryFilters(state: SearchState, getFacetConfig: GetFacetConfigByName): Dictionary<Filter[]> {
    return Object.values(state.preSelectedFilters)
      .filter(facet => getFacetConfig(facet.modelName).compulsory)
      .reduce<Dictionary<Filter[]>>((preSelectedFilters, facet) => {
        preSelectedFilters[facet.modelName] = facet.filters.filter(filter => filter.selected);
        return preSelectedFilters;
      }, {});
  }
}
